import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, Users, Mail, FileText, Loader2, Save, MailCheck, MailX, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { LOGO_URL } from "@/constants/brand";
import { api, formatApiError } from "@/lib/api";

const TABS = [
  { key: "candidature", label: "Candidature", icon: Users, testid: "admin-tab-candidature" },
  { key: "messaggi", label: "Messaggi", icon: Mail, testid: "admin-tab-messaggi" },
  { key: "template", label: "Template Email", icon: FileText, testid: "admin-tab-template" },
];

const STATUS_OPTS = [
  { key: "nuova", label: "Nuova" },
  { key: "contattato", label: "Contattato" },
  { key: "archiviata", label: "Archiviata" },
];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

export default function AdminDashboard() {
  const [tab, setTab] = useState("candidature");
  const [apps, setApps] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [tpl, setTpl] = useState({ subject: "", html: "" });
  const [loading, setLoading] = useState(true);
  const [savingTpl, setSavingTpl] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, m, t] = await Promise.all([
        api.get("/admin/applications"),
        api.get("/admin/messages"),
        api.get("/admin/email-template"),
      ]);
      setApps(a.data);
      setMsgs(m.data);
      setTpl({ subject: t.data.subject, html: t.data.html });
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("iamc_token");
        navigate("/admin");
        return;
      }
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem("iamc_token")) {
      navigate("/admin");
      return;
    }
    load();
  }, [load, navigate]);

  const logout = () => {
    localStorage.removeItem("iamc_token");
    navigate("/admin");
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/applications/${id}/status`, { status });
      setApps((list) => list.map((a) => (a.application_id === id ? { ...a, status } : a)));
      toast.success("Stato aggiornato");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setRead = async (id, letto) => {
    try {
      await api.patch(`/admin/messages/${id}/read`, { letto });
      setMsgs((list) => list.map((m) => (m.message_id === id ? { ...m, letto } : m)));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const [confirmDelete, setConfirmDelete] = useState(null);

  const deleteApp = async (id) => {
    try {
      await api.delete(`/admin/applications/${id}`);
      setApps((list) => list.filter((a) => a.application_id !== id));
      toast.success("Candidatura eliminata");
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setConfirmDelete(null);
  };

  const saveTemplate = async () => {
    setSavingTpl(true);
    try {
      await api.put("/admin/email-template", tpl);
      toast.success("Template email salvato");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingTpl(false);
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-obsidian text-white">
      <header className="sticky top-0 z-40 bg-obsidian/85 backdrop-blur-xl border-b border-brand/15">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Logo" className="w-9 h-9 rounded-xl object-cover ring-1 ring-brand/40" />
            <span className="font-display font-extrabold text-sm sm:text-base">
              PANNELLO <span className="text-brand">ADMIN</span>
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-btn"
              onClick={load}
              className="p-2.5 rounded-full border border-brand/30 text-muted2 hover:text-white hover:border-brand transition-colors duration-300"
              aria-label="Aggiorna"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="inline-flex items-center gap-2 text-sm font-bold text-muted2 hover:text-brand-coral transition-colors duration-300"
            >
              <LogOut className="w-4 h-4" /> Esci
            </button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 pb-3 flex gap-2 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={t.testid}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-colors duration-300 ${
                tab === t.key ? "bg-brand text-white" : "bg-card2 text-muted2 hover:text-white border border-brand/20"
              }`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
              {t.key === "candidature" && apps.length > 0 && (
                <span className="ml-1 bg-white/20 text-xs px-2 py-0.5 rounded-full">{apps.length}</span>
              )}
              {t.key === "messaggi" && msgs.filter((m) => !m.letto).length > 0 && (
                <span className="ml-1 bg-white/20 text-xs px-2 py-0.5 rounded-full">
                  {msgs.filter((m) => !m.letto).length}
                </span>
              )}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        {loading ? (
          <div className="flex items-center justify-center py-24" data-testid="admin-loading">
            <Loader2 className="w-8 h-8 animate-spin text-brand" />
          </div>
        ) : tab === "candidature" ? (
          <div data-testid="admin-applications-list" className="space-y-4">
            {apps.length === 0 && (
              <p className="text-muted2 text-center py-16" data-testid="admin-applications-empty">
                Nessuna candidatura ricevuta finora.
              </p>
            )}
            {apps.map((a, i) => (
              <motion.div
                key={a.application_id}
                data-testid={`admin-application-${i}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.04 }}
                className="bg-card2 border border-brand/20 rounded-2xl p-5 sm:p-6"
              >
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  <div>
                    <p className="font-display font-bold text-lg text-white">
                      {a.nome} {a.cognome}
                      <span className="ml-3 text-sm font-semibold text-muted2">{a.eta} anni · {a.citta}</span>
                    </p>
                    <p className="text-sm text-muted2 mt-1.5">
                      {a.email} · {a.telefono}
                    </p>
                    <p className="text-sm text-dim mt-1">Abita a: {a.residenza}</p>
                    <p className="text-xs text-dim mt-2 flex items-center gap-3 flex-wrap">
                      <span>{fmtDate(a.created_at)}</span>
                      {a.email_sent ? (
                        <span className="inline-flex items-center gap-1 text-[#00B894]"><MailCheck className="w-3.5 h-3.5" /> Email inviata</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-brand-coral"><MailX className="w-3.5 h-3.5" /> Email non inviata</span>
                      )}
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0 flex-wrap">
                    {STATUS_OPTS.map((s) => (
                      <button
                        key={s.key}
                        data-testid={`admin-status-${s.key}-${i}`}
                        onClick={() => setStatus(a.application_id, s.key)}
                        className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-colors duration-300 ${
                          a.status === s.key
                            ? "bg-brand text-white"
                            : "bg-surface border border-brand/20 text-muted2 hover:text-white hover:border-brand/50"
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                    {confirmDelete === a.application_id ? (
                      <button
                        data-testid={`admin-delete-confirm-${i}`}
                        onClick={() => deleteApp(a.application_id)}
                        className="px-3.5 py-1.5 rounded-full text-xs font-bold bg-red-600 hover:bg-red-500 text-white transition-colors duration-300"
                      >
                        Conferma elimina
                      </button>
                    ) : (
                      <button
                        data-testid={`admin-delete-${i}`}
                        onClick={() => {
                          setConfirmDelete(a.application_id);
                          setTimeout(() => setConfirmDelete(null), 4000);
                        }}
                        className="p-1.5 rounded-full text-muted2 hover:text-red-400 hover:bg-red-500/10 transition-colors duration-300"
                        aria-label="Elimina candidatura"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : tab === "messaggi" ? (
          <div data-testid="admin-messages-list" className="space-y-4">
            {msgs.length === 0 && (
              <p className="text-muted2 text-center py-16" data-testid="admin-messages-empty">
                Nessun messaggio ricevuto finora.
              </p>
            )}
            {msgs.map((m, i) => (
              <motion.div
                key={m.message_id}
                data-testid={`admin-message-${i}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.04 }}
                className={`bg-card2 border rounded-2xl p-5 sm:p-6 ${m.letto ? "border-brand/10 opacity-70" : "border-brand/30"}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  <div>
                    <p className="font-display font-bold text-lg text-white">{m.oggetto}</p>
                    <p className="text-sm text-muted2 mt-1">
                      {m.nome} {m.cognome} · {m.email} {m.telefono && `· ${m.telefono}`}
                    </p>
                    <p className="text-sm text-white/80 mt-3 leading-relaxed whitespace-pre-wrap">{m.messaggio}</p>
                    <p className="text-xs text-dim mt-3">{fmtDate(m.created_at)}</p>
                  </div>
                  <button
                    data-testid={`admin-message-read-${i}`}
                    onClick={() => setRead(m.message_id, !m.letto)}
                    className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-bold transition-colors duration-300 ${
                      m.letto
                        ? "bg-surface border border-brand/20 text-muted2 hover:text-white"
                        : "bg-brand text-white hover:bg-brand-hot"
                    }`}
                  >
                    {m.letto ? "Segna da leggere" : "Segna come letto"}
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <motion.div
            data-testid="admin-template-editor"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-card2 border border-brand/20 rounded-3xl p-6 sm:p-8"
          >
            <h2 className="font-display text-xl sm:text-2xl font-bold text-white mb-2">
              Email di conferma candidatura
            </h2>
            <p className="text-sm text-muted2 mb-6 leading-relaxed">
              Questa email viene inviata automaticamente a ogni candidato. Puoi usare le variabili:{" "}
              {["{{nome}}", "{{cognome}}", "{{citta}}", "{{email}}"].map((v) => (
                <code key={v} className="mx-1 px-2 py-0.5 bg-surface border border-brand/25 rounded-md text-brand text-xs">
                  {v}
                </code>
              ))}
            </p>
            <label className="block text-sm font-bold text-white mb-2">Oggetto</label>
            <input
              data-testid="template-subject-input"
              value={tpl.subject}
              onChange={(e) => setTpl((t) => ({ ...t, subject: e.target.value }))}
              className="w-full bg-surface border border-input focus:border-brand rounded-xl px-4 py-3.5 text-white outline-none transition-colors duration-300 mb-5"
            />
            <label className="block text-sm font-bold text-white mb-2">Contenuto HTML</label>
            <textarea
              data-testid="template-html-input"
              value={tpl.html}
              onChange={(e) => setTpl((t) => ({ ...t, html: e.target.value }))}
              rows={16}
              spellCheck={false}
              className="w-full bg-surface border border-input focus:border-brand rounded-xl px-4 py-3.5 text-white/90 font-mono text-xs leading-relaxed outline-none transition-colors duration-300 resize-y"
            />
            <div className="mt-6 flex justify-end">
              <button
                data-testid="template-save-btn"
                onClick={saveTemplate}
                disabled={savingTpl}
                className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hot disabled:opacity-60 text-white font-display font-bold px-7 py-3.5 rounded-full transition-colors duration-300"
              >
                {savingTpl ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Salva Template
              </button>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
